import * as React from "react";

import { cn } from "./utils";

export interface AvatarProps extends React.ComponentProps<"span"> {
  className?: string;
}

function Avatar({ className, ...props }: AvatarProps) {
  return (
    <span
      data-slot="avatar"
      className={cn(
        "relative flex size-10 shrink-0 overflow-hidden rounded-full",
        className,
      )}
      {...props}
    />
  );
}

export interface AvatarImageProps extends React.ComponentProps<"img"> {
  className?: string;
  onLoadingStatusChange?: (status: "loading" | "loaded" | "error") => void;
}

function AvatarImage({
  className,
  src,
  alt,
  onLoadingStatusChange,
  ...props
}: AvatarImageProps) {
  const [status, setStatus] = React.useState<"loading" | "loaded" | "error">("loading");

  React.useEffect(() => {
    if (!src) {
      setStatus("error");
      onLoadingStatusChange?.("error");
      return;
    }

    const image = new Image();
    image.src = src;
    image.onload = () => {
      setStatus("loaded");
      onLoadingStatusChange?.("loaded");
    };
    image.onerror = () => {
      setStatus("error");
      onLoadingStatusChange?.("error");
    };

    return () => {
      image.onload = null;
      image.onerror = null;
    };
  }, [src, onLoadingStatusChange]);

  if (status !== "loaded") {
    return null;
  }

  return (
    <img
      data-slot="avatar-image"
      src={src}
      alt={alt}
      className={cn("aspect-square size-full", className)}
      {...props}
    />
  );
}

export interface AvatarFallbackProps extends React.ComponentProps<"span"> {
  className?: string;
  delayMs?: number;
}

function AvatarFallback({
  className,
  delayMs,
  children,
  ...props
}: AvatarFallbackProps) {
  const [canRender, setCanRender] = React.useState(delayMs === undefined);

  React.useEffect(() => {
    if (delayMs !== undefined) {
      const timer = setTimeout(() => setCanRender(true), delayMs);
      return () => clearTimeout(timer);
    }
  }, [delayMs]);

  if (!canRender) {
    return null;
  }

  return (
    <span
      data-slot="avatar-fallback"
      className={cn(
        "bg-muted flex size-full items-center justify-center rounded-full",
        className,
      )}
      {...props}
    >
      {children}
    </span>
  );
}

export { Avatar, AvatarImage, AvatarFallback };
